// Client-side image downscale/re-encode before upload — keeps AI vision calls (Anthropic rejects
// any image over 10MB decoded) and S3 storage well-behaved regardless of how large the source
// photo is (a modern phone camera photo routinely runs 8-15MB). Same canvas-based
// resize-then-recompress-at-decreasing-quality approach already proven in TechSheetPanel.js, not
// shared with it directly (that file's copy is tuned for a small label thumbnail; this one targets
// a legible, AI-judgeable photo — different defaults), kept here since multiple new upload sites
// need the exact same generic logic.
export async function compressImage(file, { maxBytes = 4 * 1024 * 1024, maxDim = 2000, startQuality = 0.9 } = {}) {
  if (!file || !file.type?.startsWith('image/')) return file;
  return new Promise((resolve, reject) => {
    const img = new Image();
    const objectUrl = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(objectUrl);
      let { width, height } = img;
      if (width > maxDim || height > maxDim) {
        const ratio = Math.min(maxDim / width, maxDim / height);
        width = Math.round(width * ratio);
        height = Math.round(height * ratio);
      }
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      canvas.getContext('2d').drawImage(img, 0, 0, width, height);

      const tryQuality = (q) => {
        canvas.toBlob((blob) => {
          if (!blob) { reject(new Error('Canvas export failed')); return; }
          if (blob.size <= maxBytes || q <= 0.3) {
            resolve(new File([blob], file.name.replace(/\.[^.]+$/, '.jpg'), { type: 'image/jpeg' }));
          } else {
            tryQuality(Math.round((q - 0.1) * 10) / 10);
          }
        }, 'image/jpeg', q);
      };
      tryQuality(startQuality);
    };
    img.onerror = () => { URL.revokeObjectURL(objectUrl); reject(new Error('Could not read image')); };
    img.src = objectUrl;
  });
}
